import type { RowFieldWrites } from "./row-field-writes.ts";
import type { ListenerErrorSink } from "@tento-core/listener-errors";
import type { Option } from "@tento-lona/sheets";
import type { RowHeightPreferences } from "../preferences/row-height-preferences.ts";
import { TypedApi } from "@tento-core/typed-api";
import { Rational } from "@tento-core/rational";
import { Network } from "../network.ts";
import {
  pendingRowOf,
  type RowMirror,
  SheetRow,
  type SheetRowHandleDeps,
  sheetRowHandleFromWire,
  sheetRowHandleOf,
  wireSheetRowHandle,
} from "./sheet-row.ts";
import type { ClientLifecycle } from "../client/handle-errors.ts";
import type { SheetHost } from "../client/sheet-host.ts";
import { Actor, type InvalidationSink, SheetStructureChanged } from "../invalidation.ts";
import type { ClientContext } from "../sync/row-cell-cache.ts";
import type { SheetDataSource } from "../client/sheet-data-source.ts";
import type { DurableHandoffs } from "../client/durable-handoffs.ts";
import type { RowSyncState } from "../sync/sync-poll-entry.ts";
import { RowLocalId, SheetNetworkKey } from "@tento-lona/sheets";
import { RowId, RowKey, RowNetworkKey } from "@tento-lona/sheets";
import type { SheetId } from "@tento-lona/sheets";
import type { RowCellCache } from "../sync/row-cell-cache.ts";
import { EventsTable } from "../indices/events-table.ts";
import { RowWriteError, SdkRequestError } from "../errors.ts";
import { afterFailedWrite } from "../client/after-failed-write.ts";
import { RowAccessor } from "./rows-accessor.ts";
import { RowType } from "./row-descriptor.ts";
import { Tracing } from "@tento-core/tracing";

/**
 * Top-level row client for accessing rows by key without sheet context.
 * Accessed via `client.rows`.
 *
 * Writes go through the row the caller holds:
 * `const row = await client.rows.open(key); await row.update(patch, actor);`
 * (`row.updateDurable(patch, actor)` for the durable form,
 * `row.mergeAttributes(patch, actor)` to change part of the attribute bag,
 * the writable facade the row hands out for cells — `row.writableSingle()` /
 * `row.writableMulti()`: `write` / `moveCell` / `clear`, addressed by a
 * `CellTarget`). There is no by-key write: a surface that holds only a row
 * id opens the row (`open` answers from the client's registries without a
 * request when an open sheet holds it) and writes through it, so every
 * write of the row shares the row's one pending model.
 */
export class RowsClient {
  static readonly ACTOR = new Actor("row-client");

  readonly #api: TypedApi<Network.Routes>;
  readonly #host: SheetHost;
  readonly #invalidation: InvalidationSink;
  /** Propagates a held row's rename, recovers a promote that may have stored (`RowMirror`). */
  readonly #mirror: RowMirror;
  readonly #indexCtx: Option<ClientContext>;
  readonly #registries: {
    ids: RowLocalId.Registry;
    rows: SheetRow.Registry;
    /** The client's durable writes in flight: a refused
     *  `row.updateDurable` of a row this client opened rejects through it. */
    durableWrites: DurableHandoffs;
    /** The client's row-field ledger and transport: every row this client
     *  opens writes its own fields through it. */
    writes: RowFieldWrites;
    /** The client's listener-error sink, carried by every row it builds (absent: rethrow). */
    listenerErrors?: ListenerErrorSink;
    /** The client's row-height preferences: a draft's kept height follows its promote (absent: none kept). */
    rowHeights?: RowHeightPreferences;
    /** Whether a promote sends its client-minted row id (`SheetRowHandleDeps.clientRowIds`; absent: off). */
    clientRowIds?: () => boolean;
  };
  readonly #handleResolver: (rowId: RowId) => RowCellCache;
  readonly #dataSource: Option<SheetDataSource>;
  /** The owning client's liveness: every network verb checks it first, and
   *  every row `open` hands out carries it. */
  readonly #lifecycle: ClientLifecycle;
  /** Lists a row linked into a sheet in every open handle of it (`linkRow`). */
  readonly #listLinked: RowsClient.ListLinked;

  constructor(
    api: TypedApi<Network.Routes>,
    host: SheetHost,
    invalidation: InvalidationSink,
    /** Propagates a held row's rename and recovers a promote that may have
     *  stored (`RowMirror`); every row this client builds carries it. */
    mirror: RowMirror,
    indexCtx: Option<ClientContext>,
    /**
     * The Client-scoped registries already populated by `SheetHandle.parse`
     * for every row in every open sheet. REQUIRED — this is the client's
     * identity scope: every row this client resolves is interned
     * here and nowhere else, so two clients in one process never hand the
     * same `RowLocalId` to equal wire keys. Doubles as the cache
     * short-circuit for `open(":lookupKey")`, so reserved-row metadata
     * (`:~lona:tasks`, `:~lona:calendar:primary:events`) doesn't need a
     * separate `GET /api/rows/{rowKey}` roundtrip; a registry MISS still
     * falls through to the server fetch.
     */
    registries: {
      ids: RowLocalId.Registry;
      rows: SheetRow.Registry;
      /** The client's durable writes in flight: a refused
       *  `row.updateDurable` of a row this client opened rejects through it. */
      durableWrites: DurableHandoffs;
      /** The client's row-field ledger and transport (`RowFieldWrites`). */
      writes: RowFieldWrites;
      /** The client's listener-error sink, carried by every row it builds (absent: rethrow). */
      listenerErrors?: ListenerErrorSink;
      /** The client's row-height preferences: a draft's kept height follows its promote (absent: none kept). */
      rowHeights?: RowHeightPreferences;
      /** Whether a promote sends its client-minted row id (`SheetRowHandleDeps.clientRowIds`; absent: off). */
      clientRowIds?: () => boolean;
    },
    /**
     * Resolves RowHandles through the client-scoped backend registry
     * (`(rowId) => backend.rowHandle(rowId)`). Wired onto every
     * Client this RowsClient builds so standalone rows share one
     * handle per cell-access key with sheet accessors — without it,
     * local edits land in private handles invisible to renderers
     * (the split-store bug class).
     */
    handleResolver: (rowId: RowId) => RowCellCache,
    /** Client-scoped cell data source; answers `peekSyncState`. */
    dataSource: Option<SheetDataSource>,
    /**
     * The owning client's liveness (`LonaSdk.Client.Lifecycle`): after its
     * `dispose` every verb here — and every network verb of a row `open`
     * handed out, also one held from before — throws
     * `LonaSdk.Client.DisposedError`; an `open` it overtook hands out no
     * row.
     */
    lifecycle: ClientLifecycle,
    /**
     * Lists the row `lookupKey` names in every open handle of `sheetId`
     * (each re-reads its sheet and adopts the row, its insert announced as
     * `actor`): what a landed `linkRow` — or one that may have landed —
     * runs.
     */
    listLinked: RowsClient.ListLinked,
  ) {
    this.#lifecycle = lifecycle;
    this.#listLinked = listLinked;
    this.#api = api;
    this.#host = host;
    this.#invalidation = invalidation;
    this.#mirror = mirror;
    this.#indexCtx = indexCtx;
    this.#registries = registries;
    this.#handleResolver = handleResolver;
    this.#dataSource = dataSource;
  }

  /** Get sync state for a row across all opened sheets. */
  peekSyncState(rowId: RowLocalId): Option<RowSyncState.Detail> {
    return this.#dataSource?.peekSyncState(rowId) ?? null;
  }

  /**
   * The row `rowKey` names as the client holds it now — an open sheet's row
   * (its registries), else `null` — never a request: the synchronous form of
   * `open` for a surface that must write the row in the same turn it names
   * it (a renderer's attribute callback, whose rebind reads the bag right
   * after). A row the client does not hold is `open`ed.
   */
  peek(rowKey: RowKey): Option<SheetRow.Handle> {
    this.#lifecycle.assertLive("rows.peek");
    const localId = this.#registries.ids.get(rowKey);
    if (localId == null) return null;
    const sheetRow = this.#registries.rows.get(localId);
    if (sheetRow == null) return null;
    const row = sheetRowHandleOf(sheetRow, this.#rowDeps());
    this.#wireRowHandleInit(row, sheetRow.raw);
    return row;
  }

  /**
   * Open a row by key (`RowKey.of(raw)` for a key held as text; a
   * cross-user key `[user@email]:lookupKey` parses too). `null` when the
   * server has no such row (404); any other failure throws `SdkRequestError`
   * (boundary `rows`, step `get`).
   */
  async open(rowKey: RowKey): Promise<Option<SheetRow.Handle>> {
    this.#lifecycle.assertLive("rows.open");
    // Cache short-circuit via the existing Client-scoped registries.
    // `SheetHandle.parse` populates `deps.ids` (RowLocalId.Registry)
    // + `deps.rows` (SheetRow.Registry) for every row in an open
    // sheet, so reserved keys like `:~lona:tasks` are already
    // resolvable without a separate `GET /api/rows/{rowKey}`. The
    // HAR audit at `__llm_reference/debug.har` showed two such
    // bare-row GETs firing during a calendar render where the
    // sheet open had already populated those registries.
    const held = this.peek(rowKey);
    if (held != null) return held;
    const raw = await SdkRequestError.checkRequest(
      "rows",
      "get",
      "GET",
      "/api/rows/{rowKey}",
      () =>
        SdkRequestError.notFoundAsNull(() =>
          this.#api.getResult("/api/rows/{rowKey}", {
            path: { rowKey: RowNetworkKey.forRead(rowKey) },
          })
        ),
    );
    // The client may have been disposed while the GET was out: hand out no
    // row then.
    this.#lifecycle.assertLive("rows.open");
    if (!raw) return null;
    return this.#buildClientFromMeta(raw);
  }

  /**
   * A pending row of wire type `type` (`SheetRow.Pending`): a local draft,
   * not yet persisted — the row a create-row form drafts into. Its
   * `update`s buffer on it (applied at once) and settle once
   * `draft.promote(sheetId, position, actor)` persisted it, which resolves
   * the created row's `SheetRow.Handle`. Interned in this client's identity
   * scope; throws `LonaSdk.Client.DisposedError` once the client is
   * disposed.
   */
  pending<T extends string>(type: T): SheetRow.Pending<T> {
    this.#lifecycle.assertLive("rows.pending");
    return pendingRowOf(
      type,
      this.#rowDeps(),
      this.#registries.ids,
      (row, raw) => this.#wireRowHandleInit(row, raw),
      this.#registries.rowHeights ?? null,
    );
  }

  /** What every row this client builds is built with. */
  #rowDeps(): SheetRowHandleDeps {
    return {
      api: this.#api,
      host: this.#host,
      invalidation: this.#invalidation,
      lifecycle: this.#lifecycle,
      durableWrites: this.#registries.durableWrites,
      handleResolver: this.#handleResolver,
      mirror: this.#mirror,
      writes: this.#registries.writes,
      ...(this.#registries.listenerErrors != null ? { listenerErrors: this.#registries.listenerErrors } : {}),
      ...(this.#registries.clientRowIds != null ? { clientRowIds: this.#registries.clientRowIds } : {}),
    };
  }

  #buildClientFromMeta(meta: Network.SheetRowDb): SheetRow.Handle {
    const client = sheetRowHandleFromWire(meta, this.#rowDeps(), this.#registries.ids);
    this.#wireRowHandleInit(client, meta);
    return client;
  }

  /**
   * Wire the row handle's lazy init to apply the row's raw metadata
   * and dispatch event-bearing rows to the client-scoped EventsTable.
   * Shared between the cache-hit path (registry-resolved
   * `SheetRow.raw`, a `Network.SheetRowDb`) and the cache-miss path
   * (`Network.SheetRow` from the GET response).
   */
  #wireRowHandleInit(
    client: SheetRow.Handle,
    raw: Network.SheetRowDb,
  ): void {
    const ctx = this.#indexCtx;
    const onRowHandleInit = (rh: RowCellCache): void => {
      if (ctx) rh.registry = ctx.encodings;
      rh.applyMeta(raw, RowType.get(raw.type).cellCardinality);
      if (ctx) {
        const eventsTable = ctx.eventsTable;
        if (eventsTable && EventsTable.eventBearingRowTypes.includes(raw.type)) {
          const rowLocalId = this.#registries.ids.resolveFromRawId(
            rh.rowId.toString(),
          );
          const isAggregator =
            raw.type === "calendar-list" ||
            raw.type === "alias.calendar" ||
            raw.type === "alias.calendar-list";
          eventsTable.attachRow(rh, { rowLocalId, isAggregator });
        }
      }
    };
    wireSheetRowHandle(client, {
      onRowHandleInit,
      // A standalone row: no sheet and no parent resolver; the client's
      // lifecycle guards its network verbs.
      resolveParent: null,
    });
  }

  /**
   * Check whether a lookup key is available (not already in use), ignoring
   * `excludeRowId` (the row being renamed) when set. Throws `SdkRequestError`
   * (boundary `rows`, step `check-exists`) on a server failure.
   */
  async checkLookupKeyExists(
    rowKey: RowKey,
    excludeRowId: Option<RowId>,
  ): Promise<Network.LookupKeyExistsResponse> {
    this.#lifecycle.assertLive("rows.checkLookupKeyExists");
    return SdkRequestError.checkRequest(
      "rows",
      "check-exists",
      "GET",
      "/api/rows/{rowKey}/exists",
      () =>
        this.#api.getResult("/api/rows/{rowKey}/exists", {
          path: { rowKey: RowNetworkKey.of(rowKey) },
          query: { excludeRowId: excludeRowId == null ? null : RowNetworkKey.of(excludeRowId) },
        }),
    );
  }

  /**
   * Link a reserved-key row (by its lookup key, `RowKey.keyed(":~lona:tasks")`)
   * to the sheet `sheetId`; a `null` position appends. Resolves once the
   * server linked it and every open handle of the sheet lists it: each
   * re-reads its sheet and adopts the linked row (its insert announced as
   * `actor`; a re-read that fails is traced — the link stands), then the
   * link emits `SheetStructureChanged` (scoped to `sheetId`) stamped with
   * `actor`. Throws `RowWriteError("link")` on a server failure; unanswered
   * (`SdkRequestError.mayHaveStored`), the open handles re-read their sheet
   * all the same and list the row when the server holds it
   * (`RowAccessor.ACTOR`).
   */
  async linkRow(
    sheetId: SheetId,
    lookupKey: RowKey.Keyed,
    position: Option<Rational>,
    actor: Actor,
  ): Promise<void> {
    this.#lifecycle.assertLive("rows.linkRow");
    try {
      await RowWriteError.check(
        "link",
        "POST",
        "/api/rows/linkable",
        () =>
          this.#api.postResult("/api/rows/linkable", {
            // An absent position stays off the wire (server appends).
            body: {
              sheetId: SheetNetworkKey.of(sheetId),
              lookupKey: RowNetworkKey.of(lookupKey),
              position: position ?? null,
            },
          }),
      );
    } catch (err) {
      // No verdict (or one the SDK could not read): the link may have
      // landed — the open handles of the sheet re-read it and list the row.
      afterFailedWrite(err, this.#lifecycle, "rows.linkRow", () => this.#listLinked(sheetId, lookupKey, RowAccessor.ACTOR));
      throw err;
    }
    try {
      await this.#listLinked(sheetId, lookupKey, actor);
    } catch (failure) {
      // Linked: the link stands whatever the re-read does.
      Tracing.warn("net")(`rows.linkRow: listing the linked row failed: ${String(failure)}`);
    }
    this.#invalidation.emit(new SheetStructureChanged(actor, sheetId));
  }

  /**
   * List all linkable rows (reserved-key rows that can be attached to
   * sheets). Throws `SdkRequestError` (boundary `rows`, step
   * `list-linkable`) on a server failure.
   */
  async listLinkable(): Promise<Network.LinkableRow[]> {
    this.#lifecycle.assertLive("rows.listLinkable");
    return SdkRequestError.checkRequest(
      "rows",
      "list-linkable",
      "GET",
      "/api/rows/linkable",
      () => this.#api.getResult("/api/rows/linkable"),
    );
  }
}

/** Types that go with `RowsClient`: `ListLinked`, `Opener`. */
export namespace RowsClient {
  /**
   * Lists the row `lookupKey` names in every open handle of `sheetId`
   * (`linkRow`'s listing, the client's), its insert announced as `actor`.
   */
  export type ListLinked = (sheetId: SheetId, lookupKey: RowKey.Keyed, actor: Actor) => Promise<void>;

  /**
   * What a client that opens rows by key takes (the tasks and events
   * clients): `open` a row by its `RowKey`. A `RowsClient` satisfies it, and
   * so does a test's double (`{ open: (key) => … } satisfies
   * RowsClient.Opener`), which then states the key it answers for.
   */
  export interface Opener {
    open(rowKey: RowKey): Promise<Option<SheetRow.Handle>>;
  }
}
